/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: components\AiAgentContextTab.tsx
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the AI Integration module.
────────────────────────────────────────────────────────────────*/

import React from 'react';
import { Building2, ListChecks, Shield } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Toggle } from '../../../components/ui/Toggle';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { Label } from '../../../components/ui/label';
import { useI18n } from '../../../hooks/useI18n';

interface ContextFormData {
  company_name: string;
  products: string[];
  key_points: string;
  disclosure_enabled: boolean;
  pii_redaction_enabled: boolean;
  disclosure_message: string;
  persona_name: string;
  role_description: string;
  greeting_message: string;
  do_rules: string[];
  dont_rules: string[];
  closing_message: string;
}

interface AiAgentContextTabProps {
  formData: ContextFormData;
  onChange: (field: keyof ContextFormData, value: unknown) => void;
  showSection?: 'context' | 'instructions' | 'both';
  agentType?: 'voice' | 'chat';
}

export function AiAgentContextTab({ formData, onChange, showSection = 'both', agentType = 'voice' }: AiAgentContextTabProps) {
  const { t } = useI18n();

  return (
    <>
      {/* Context FormBox */}
      {(showSection === 'context' || showSection === 'both') && (
        <FormBox title={t('aiAgents.tabs.context') || 'Context'} icon={<Building2 className="h-5 w-5" />} className="mt-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
          <SimpleTooltip content={t('aiAgents.form.companyNameTooltip') || 'Organization the AI represents'} position="top">
            <Label className="text-left">{t('aiAgents.form.companyName') || 'Company/Organization'}</Label>
          </SimpleTooltip>
          <input type="text" value={formData.company_name}
            onChange={e => onChange('company_name', e.target.value)}
            className="md:col-span-3 px-3 py-2 bg-background border border-input rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary"
            placeholder="e.g., VitalPBX, Ring2All LLC" />

          <SimpleTooltip content={t('aiAgents.form.productsTooltip') || 'Products or services the AI should know about'} position="top">
            <Label className="text-left">{t('aiAgents.form.products') || 'Products/Services'}</Label>
          </SimpleTooltip>
          <textarea value={formData.products.join('\n')}
            onChange={(e) => { const items = e.target.value.split('\n').map(s => s.trim()).filter(s => s); onChange('products', items); }}
            rows={3}
            className="md:col-span-3 px-3 py-2 bg-background border border-input rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary resize-y"
            placeholder={"e.g.,\nProduct A: Premium VoIP Service\nProduct B: SIP Trunking"} />

          <SimpleTooltip content={t('aiAgents.form.keyPointsTooltip') || 'Important information for the AI to remember'} position="top">
            <Label className="text-left">{t('aiAgents.form.keyPoints') || 'Key Information'}</Label>
          </SimpleTooltip>
          <textarea value={formData.key_points}
            onChange={e => onChange('key_points', e.target.value)}
            rows={3}
            className="md:col-span-3 px-3 py-2 bg-background border border-input rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary resize-y"
            placeholder="e.g., Hours: 9-5 Mon-Fri, Returns within 30 days" />
        </div>

        <div className="mt-6 pt-6 border-t border-border">
          <h3 className="text-sm font-medium text-foreground mb-4">{t('aiAgents.tabs.security') || 'Security & Compliance'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
            <SimpleTooltip content={t('aiAgents.form.aiDisclosureTooltip')} position="top">
              <Label className="text-left">{t('aiAgents.form.aiDisclosure') || 'AI Disclosure'}</Label>
            </SimpleTooltip>
            <Toggle value={formData.disclosure_enabled} onChange={(v) => onChange('disclosure_enabled', v)} />
            <SimpleTooltip content={t('aiAgents.form.piiRedactionTooltip')} position="top">
              <Label className="text-left">{t('aiAgents.form.piiRedaction') || 'PII Redaction'}</Label>
            </SimpleTooltip>
            <Toggle value={formData.pii_redaction_enabled} onChange={(v) => onChange('pii_redaction_enabled', v)} />
          </div>
          {formData.disclosure_enabled && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
              <SimpleTooltip content={t('aiAgents.form.disclosureMessageTooltip')} position="top">
                <Label className="text-left">{t('aiAgents.form.disclosureMessage') || 'Disclosure Message'}</Label>
              </SimpleTooltip>
              <input type="text" value={formData.disclosure_message}
                onChange={e => onChange('disclosure_message', e.target.value)}
                className="md:col-span-3 px-3 py-2 bg-background border border-input rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary"
                placeholder={agentType === 'chat' ? "You are now chatting with an AI assistant." : "You are now speaking with an AI assistant."} />
            </div>
          )}
        </div>
      </FormBox>
      )}

      {/* Instructions FormBox */}
      {(showSection === 'instructions' || showSection === 'both') && (
        <FormBox title={t('aiAgents.tabs.instructions') || 'Instructions'} icon={<ListChecks className="h-5 w-5" />} className="mt-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-start">
          <SimpleTooltip content={t('aiAgents.form.personaNameTooltip') || 'The name the AI uses to introduce itself'} position="top">
            <Label className="text-left">{t('aiAgents.form.personaName') || 'Display Name'}</Label>
          </SimpleTooltip>
          <input type="text" value={formData.persona_name}
            onChange={e => onChange('persona_name', e.target.value)}
            className="md:col-span-3 px-3 py-2 bg-background border border-input rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary"
            placeholder="e.g., Maya, Alex" />

          <SimpleTooltip content={t('aiAgents.form.roleDescriptionTooltip') || 'What the AI agent does and its purpose'} position="top">
            <Label className="text-left">{t('aiAgents.form.roleDescription') || 'Role Description'}</Label>
          </SimpleTooltip>
          <textarea value={formData.role_description}
            onChange={e => onChange('role_description', e.target.value)}
            rows={3}
            className="md:col-span-3 px-3 py-2 bg-background border border-input rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary resize-y"
            placeholder="e.g., AI assistant for customer support" />

          <SimpleTooltip content={t('aiAgents.form.greetingMessageTooltip')} position="top">
            <Label className="text-left">{t('aiAgents.form.greetingMessage') || 'Greeting Message'}</Label>
          </SimpleTooltip>
          <input type="text" value={formData.greeting_message}
            onChange={e => onChange('greeting_message', e.target.value)}
            className="md:col-span-3 px-3 py-2 bg-background border border-input rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary"
            placeholder="Hello! How can I help you today?" />

          <SimpleTooltip content={t('aiAgents.form.doRulesTooltip') || 'Things the AI should do'} position="top">
            <Label className="text-left text-green-600">{t('aiAgents.form.doRules') || '✓ DO (Things to do)'}</Label>
          </SimpleTooltip>
          <div className="md:col-span-3">
            <textarea value={formData.do_rules.join('\n')}
              onChange={(e) => onChange('do_rules', e.target.value.split('\n'))}
              className="w-full h-32 px-3 py-2 bg-background border border-green-500/30 rounded-md focus:ring-2 focus:ring-green-500/20 focus:border-green-500 text-sm resize-y overflow-auto"
              placeholder="Enter rules, one per line..." />
          </div>

          <SimpleTooltip content={t('aiAgents.form.dontRulesTooltip') || "Things the AI should avoid"} position="top">
            <Label className="text-left text-red-600">{t('aiAgents.form.dontRules') || "✗ DON'T (Things to avoid)"}</Label>
          </SimpleTooltip>
          <div className="md:col-span-3">
            <textarea value={formData.dont_rules.join('\n')}
              onChange={(e) => onChange('dont_rules', e.target.value.split('\n'))}
              className="w-full h-32 px-3 py-2 bg-background border border-red-500/30 rounded-md focus:ring-2 focus:ring-red-500/20 focus:border-red-500 text-sm resize-y overflow-auto"
              placeholder="Enter things to avoid, one per line..." />
          </div>

          <SimpleTooltip content={t('aiAgents.form.closingMessageTooltip') || 'How the AI should end conversations'} position="top">
            <Label className="text-left">{t('aiAgents.form.closingMessage') || 'Closing Behavior'}</Label>
          </SimpleTooltip>
          <input type="text" value={formData.closing_message}
            onChange={e => onChange('closing_message', e.target.value)}
            className="md:col-span-3 px-3 py-2 bg-background border border-input rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary"
            placeholder={agentType === 'chat' ? "e.g., Thank them for chatting and offer further assistance" : "e.g., Thank them for calling and offer further assistance"} />
        </div>
      </FormBox>
      )}
    </>
  );
}
